import { createOptionRowTenor } from "api/pricerApi";
import {
  defaultMonthsInTenorForPeriod,
  suggestDatesForTenor,
} from "helpers/Pricer/addTenorTenorRules";
import { toast } from "helpers/generalHelper";
import { toastValidationErrors } from "helpers/toastValidationErrors";
import dayjs from "dayjs";

/**
 * @param {import('axios').AxiosResponse} response
 */
export function getCreateTenorToastMessage(response) {
  const message = response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;

  const data = response?.data?.data;
  if (data?.updated_tenor) return "Tenor updated successfully";
  if (data?.created_tenor ?? data?.created) return "Tenor created successfully";
  return "Tenor saved";
}

/**
 * @param {import('axios').AxiosResponse} response
 */
export function wasTenorNewlyCreated(response) {
  const data = response?.data?.data;
  if (data?.updated_tenor) return false;
  if (data?.created_tenor != null) return Boolean(data.created_tenor);
  if (data?.created != null) return Boolean(data.created);
  return false;
}

/**
 * @param {import('axios').AxiosResponse} response
 */
export function wasTenorUpdated(response) {
  return Boolean(response?.data?.data?.updated_tenor);
}

/**
 * @param {unknown} sublegMonths
 * @returns {string[]}
 */
export function normalizeSublegMonthLabels(sublegMonths) {
  if (!Array.isArray(sublegMonths)) return [];

  return sublegMonths
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        return String(
          item.subleg_month ??
            item.month ??
            item.tenor ??
            item.label ??
            item.value ??
            ""
        ).trim();
      }
      return "";
    })
    .filter(Boolean);
}

/**
 * @param {unknown} tenorMonths
 * @returns {{ id?: number, subleg_month: string|null, start_date: string|null, end_date: string|null }[]}
 */
export function normalizeTenorMonths(tenorMonths) {
  if (!Array.isArray(tenorMonths)) return [];

  return tenorMonths.map((item) => ({
    id: item?.id,
    subleg_month:
      item?.subleg_month != null && String(item.subleg_month).trim()
        ? String(item.subleg_month).trim()
        : null,
    start_date: item?.start_date ?? null,
    end_date: item?.end_date ?? null,
  }));
}

/**
 * @param {unknown} items
 * @param {boolean} exists
 */
function mapTenorCatalogItems(items, exists) {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const tenor = String(item?.tenor ?? "").trim();
      if (!tenor) return null;

      const isExisting = item?.exists === true || exists === true;

      return {
        value: tenor,
        label: isExisting ? `${tenor} · exists` : tenor,
        tenor,
        date: item?.date ?? null,
        period_id: item?.period_id,
        sublegs: item?.sublegs ?? null,
        subleg_months: normalizeSublegMonthLabels(item?.subleg_months),
        exists: isExisting,
        link: normalizeExpiryCalendarLink(item?.link),
        default_underlying_tenor: String(
          item?.default_underlying_tenor ?? ""
        ).trim(),
        style_period_tenor_id: isExisting
          ? item?.style_period_tenor_id ?? null
          : null,
        underlying_tenor: isExisting ? item?.underlying_tenor ?? null : null,
        expiry_date: isExisting ? item?.expiry_date ?? null : null,
        tenor_months: isExisting ? normalizeTenorMonths(item?.tenor_months) : [],
      };
    })
    .filter(Boolean);
}

function sortTenorOptionsByDate(options) {
  return [...options].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
}

/**
 * Expiry-calendar URL. Lookup-only — never POST it.
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeExpiryCalendarLink(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return trimmed;
  } catch {
    return null;
  }
}

const EXPIRY_LINK_VARIANTS = new Set([
  "default",
  "cal",
  "cal_mar",
  "cal_jun",
  "cal_sep",
]);

/**
 * Stored calendar pages for a PCS + period. Most combinations have one
 * `default` URL. Early-expiry calendars store Cal / Mar / Jun / Sep.
 * @param {unknown} links
 * @returns {{ variant: string, label: string, url: string }[]}
 */
export function normalizeExpiryLinks(links) {
  if (!Array.isArray(links)) return [];

  return links
    .map((item) => {
      const url = normalizeExpiryCalendarLink(item?.url);
      if (!url) return null;
      const variant = EXPIRY_LINK_VARIANTS.has(item?.variant)
        ? item.variant
        : "default";
      const label =
        typeof item?.label === "string" && item.label.trim()
          ? item.label.trim()
          : "Link";
      return { variant, label, url };
    })
    .filter(Boolean);
}

/**
 * Link cell contents. One page shows before a tenor is picked. Several
 * pages stay blank until the selected tenor's own `link` identifies which
 * early-expiry calendar to open — never all four at once.
 * @param {Array<{ variant?: string, label?: string, url?: string }>|null|undefined} links
 * @param {string|null|undefined} selectedTenorLink
 * @returns {{ label: string, url: string }|null}
 */
export function resolveVisibleExpiryCalendarLink(links, selectedTenorLink) {
  const normalized = normalizeExpiryLinks(links);
  if (!normalized.length) return null;
  if (normalized.length === 1) return normalized[0];

  const url = normalizeExpiryCalendarLink(selectedTenorLink);
  if (!url) return null;

  return normalized.find((item) => item.url === url) ?? { label: "Link", url };
}

/**
 * Prefer `links`. A missing array still accepts the older single `link`
 * string so a response from before the variant list does not blank the cell.
 * An explicit empty `links` array stays blank.
 * @param {object} [data]
 */
function expiryLinksFromPayload(data) {
  if (Array.isArray(data?.links)) return normalizeExpiryLinks(data.links);

  const legacy = normalizeExpiryCalendarLink(data?.link);
  if (!legacy) return [];
  return [{ variant: "default", label: "Link", url: legacy }];
}

/**
 * @param {import('axios').AxiosResponse} [response]
 * @param {string} [periodLabel]
 */
function mapTenorOptionsFromPayload(data) {
  if (Array.isArray(data?.tenor_options)) {
    return sortTenorOptionsByDate(mapTenorCatalogItems(data.tenor_options, false));
  }

  const missing = mapTenorCatalogItems(data.missing_tenors, false);
  const existing = mapTenorCatalogItems(data.existing_tenors, true);
  return sortTenorOptionsByDate([...existing, ...missing]);
}

export function mapMissingTenorsResponse(response, periodLabel = "") {
  const data = response?.data?.data ?? {};
  const options = mapTenorOptionsFromPayload(data);
  const ulOptions = mapTenorCatalogItems(data.missing_ul_tenors, false).map(
    (item) => ({
      value: item.value,
      label: item.tenor,
      tenor: item.tenor,
      date: item.date,
      period_id: item.period_id,
      sublegs: item.sublegs,
      subleg_months: item.subleg_months,
    })
  );
  const firstMissingOption = options.find((item) => !item.exists);
  const apiSublegs = data.sublegs ?? firstMissingOption?.sublegs;
  const fromApi = Number(apiSublegs);
  const defaultSublegs =
    Number.isFinite(fromApi) && fromApi >= 1
      ? fromApi
      : defaultMonthsInTenorForPeriod(periodLabel);

  return {
    options,
    ulOptions,
    lastExistingTenor: data.last_existing_tenor ?? null,
    defaultSublegs,
    enableStartDate: Boolean(data.enable_start_date),
    links: expiryLinksFromPayload(data),
    productId: data.product_id,
    contractId: data.contract_id,
    styleId: data.style_id,
    periodId: data.period_id,
  };
}

/**
 * Ant Select onChange may drop extra option fields — resolve from the loaded list.
 * @param {{ value?: string }[]} options
 * @param {string} value
 * @param {object} [fallbackOption]
 */
export function pickSelectedMissingTenorOption(options, value, fallbackOption) {
  return (
    (Array.isArray(options)
      ? options.find((item) => item.value === value)
      : null) ??
    fallbackOption ??
    null
  );
}

/**
 * @param {object} [row]
 * @param {object} [option]
 * @param {number} [defaultSublegs]
 */
export function resolveMonthsInTenor(row, option, defaultSublegs) {
  if (option?.exists === true) {
    const saved = Array.isArray(option.tenor_months)
      ? option.tenor_months.length
      : 0;
    if (saved >= 1) return saved;
  }

  const fromOption = Number(option?.sublegs);
  if (Number.isFinite(fromOption) && fromOption >= 1) return fromOption;

  const fromDefault = Number(defaultSublegs);
  if (Number.isFinite(fromDefault) && fromDefault >= 1) return fromDefault;

  const fromRow = Number(row?.months_in_tenor);
  if (Number.isFinite(fromRow) && fromRow >= 1) return fromRow;

  return defaultMonthsInTenorForPeriod(row?.period_label);
}

export function getMissingTenorsAvailabilityHint(
  { lastExistingTenor, options } = {},
  periodLabel = ""
) {
  const count = options?.length ?? 0;
  const period = String(periodLabel ?? "").trim();
  const periodSuffix = period ? ` (${period})` : "";

  if (!count) {
    return lastExistingTenor
      ? `No tenors after ${lastExistingTenor} for this period.`
      : "No tenors for this product, contract, style, and period.";
  }

  const next =
    options.find((item) => !item.exists)?.tenor ??
    options.find((item) => !item.exists)?.value ??
    options[0]?.tenor ??
    options[0]?.value;

  return lastExistingTenor
    ? `${count} tenor(s)${periodSuffix}. Last existing: ${lastExistingTenor}. Next: ${next}.`
    : `${count} tenor(s)${periodSuffix}. Next: ${next}.`;
}

/**
 * Build row patch when the user picks a tenor from the missing-tenors API.
 * Existing (`exists: true`) prefills saved dates; missing is a create.
 * @param {object} row
 * @param {object} [option]
 * @param {string} [shortcutLabel]
 */
export function buildTenorSelectionPatch(
  row,
  option,
  shortcutLabel = "",
  defaultSublegs
) {
  const exists = option?.exists === true;
  const label = String(option?.tenor ?? option?.value ?? "").trim();
  const monthsInTenor = resolveMonthsInTenor(row, option, defaultSublegs);
  const underlying = String(option?.underlying_tenor ?? "").trim();
  const defaultUnderlying = String(
    option?.default_underlying_tenor ?? ""
  ).trim();

  const patch = {
    style_period_tenor_id: exists ? option?.style_period_tenor_id ?? null : null,
    tenor_exists: exists,
    tenor_label: label,
    tenor_date: null,
    end_date: null,
    start_date: row.enable_start_date ? row.start_date : null,
    ul_tenor: exists ? underlying : defaultUnderlying,
    months_in_tenor: monthsInTenor,
    sublegs_from_api: option?.sublegs != null || exists,
    sub_leg: "",
    sub_legs: [],
    tenor_group_id: null,
    sub_leg_index: 0,
  };

  if (option?.date) {
    const apiDate = dayjs(option.date);
    if (apiDate.isValid()) {
      patch.tenor_date = apiDate;
    }
  }

  if (!exists && row.enable_start_date && label) {
    const dates = suggestDatesForTenor(label, row.period_label, true);
    if (dates.start_date) {
      patch.start_date = dates.start_date;
    }
  }

  return patch;
}

/**
 * @param {import('./buildCreateTenorRequest').CreateTenorRequest} payload
 */
export async function ensureLiveVolSurfaceTenor(payload) {
  const res = await createOptionRowTenor(payload);
  toast(getCreateTenorToastMessage(res), "success");
  return res?.data?.data;
}

/**
 * Laravel 422 body: `{ message, errors: { field: ["…"] } }`.
 * Prefer the field messages (same strings the rest of the app toasts).
 * @param {object} [data]
 * @returns {string[]}
 */
export function laravelValidationMessages(data) {
  const fieldErrors = data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const lines = Object.values(fieldErrors)
      .flat()
      .map((m) => String(m ?? "").trim())
      .filter(Boolean);
    if (lines.length) return [...new Set(lines)];
  }

  const message = typeof data?.message === "string" ? data.message.trim() : "";
  return message ? [message] : [];
}

export function handleCreateTenorError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 422) {
    const lines = laravelValidationMessages(data);
    toastValidationErrors(lines.join("\n") || "Validation failed", {
      title: "Tenor save failed",
      variant: "warning",
    });
    return;
  }

  if (data?.message) {
    toast(data.message, "error");
    return;
  }

  // Interceptor already toasted and wrapped the Axios error (no `.response`).
  if (!error?.response) return;

  toast("Failed to save tenor", "error");
}
